import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ loading: true });
    const { refresh } = useAuth();
    const tries = useRef(0);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;

        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setStatus({ loading: false, paid: true, data });
                    refresh();
                } else if (["expired", "failed"].includes(data.payment_status)) {
                    setStatus({ loading: false, paid: false, data });
                } else if (tries.current >= 12) {
                    setStatus({ loading: false, paid: false, timeout: true, data });
                } else {
                    tries.current += 1;
                    setTimeout(poll, 2000);
                }
            } catch (e) {
                if (!cancelled) setStatus({ loading: false, error: formatApiError(e) });
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId, refresh]);

    if (!sessionId) {
        return (
            <main className="max-w-md mx-auto p-8">
                <div className="bg-white brut-static rounded-xl p-6">No session provided.</div>
            </main>
        );
    }

    return (
        <main className="max-w-md mx-auto p-6 py-16">
            <div className="bg-white brut-static rounded-xl p-8 text-center" data-testid="payment-success-card">
                {status.loading ? (
                    <>
                        <Loader2 className="w-10 h-10 mx-auto animate-spin mb-3" />
                        <h1 className="display text-2xl font-black">Confirming your payment…</h1>
                        <p className="text-sm text-[#4A4A4A] mt-2">This usually takes a couple of seconds.</p>
                    </>
                ) : status.paid ? (
                    <>
                        <div className="w-14 h-14 mx-auto rounded-full bg-[#FFE55C] border-2 border-[#0A0A0A] flex items-center justify-center mb-3">
                            <CheckCircle2 className="w-7 h-7" />
                        </div>
                        <h1 className="display text-3xl font-black mb-1">You're all set.</h1>
                        <p className="text-sm text-[#4A4A4A] font-medium">
                            {status.data?.kind === "subscription"
                                ? "Your plan is now active."
                                : status.data?.kind === "tip"
                                ? "Tip delivered — that made someone's day."
                                : "Your purchase is confirmed."}
                        </p>
                        <div className="mt-6 flex gap-2 justify-center">
                            <Link to="/" className="px-4 py-2 rounded-lg brut bg-[#FFB8D1] font-bold" data-testid="success-back-home">Back to feed</Link>
                        </div>
                    </>
                ) : (
                    <>
                        <XCircle className="w-10 h-10 mx-auto mb-3 text-[#FF5A5F]" />
                        <h1 className="display text-2xl font-black">Something's off.</h1>
                        <p className="text-sm text-[#4A4A4A] mt-2">
                            {status.timeout ? "Payment is still processing. Refresh in a minute." : status.error || "Payment not completed."}
                        </p>
                        <div className="mt-6 flex gap-2 justify-center">
                            <Link to="/pricing" className="px-4 py-2 rounded-lg brut-inset bg-white font-bold">Try again</Link>
                            <Link to="/" className="px-4 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold">Home</Link>
                        </div>
                    </>
                )}
            </div>
        </main>
    );
}

export function PaymentCancel() {
    const navigate = useNavigate();
    useEffect(() => { toast.info("Payment canceled — no charge was made."); }, []);
    return (
        <main className="max-w-md mx-auto p-6 py-16">
            <div className="bg-white brut-static rounded-xl p-8 text-center" data-testid="payment-cancel-card">
                <XCircle className="w-10 h-10 mx-auto mb-3 text-[#FF5A5F]" />
                <h1 className="display text-2xl font-black">No worries.</h1>
                <p className="text-sm text-[#4A4A4A] mt-2">You weren't charged. Come back anytime.</p>
                <div className="mt-6 flex gap-2 justify-center">
                    <button onClick={() => navigate("/pricing")} className="px-4 py-2 rounded-lg brut-inset bg-white font-bold">Back to pricing</button>
                    <button onClick={() => navigate("/")} className="px-4 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold">Home</button>
                </div>
            </div>
        </main>
    );
}
